import React, { useMemo } from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import MuiAvatar, { AvatarProps as MuiAvatarProps } from '@material-ui/core/Avatar';
import { fade } from '@material-ui/core/styles/colorManipulator';
import PersonIcon from '@material-ui/icons/Person';
import classnames from 'classnames';

import { Tooltip } from './Tooltip';

const styles = ({
  custom: {
    palette: { colorDarkGray },
    fontSizes: { small, bodyG, subHeading },
  },
}: Theme) =>
  createStyles({
    sizeXSmall: {
      height: 26,
      width: 26,
      fontSize: small,
    },
    sizeSmall: {
      height: 29,
      width: 29,
      fontSize: small,
    },
    sizeLarge: {
      height: 40,
      width: 40,
      fontSize: bodyG,
    },
    sizeXLarge: {
      height: 64,
      width: 64,
      fontSize: bodyG,
    },
    personIcon: {
      color: colorDarkGray,
      fontSize: subHeading,
    },
    button: {
      cursor: 'pointer',
    },
  });

export interface AvatarProps extends Omit<MuiAvatarProps, 'classes'>, WithStyles<typeof styles> {
  variant?: 'default' | 'group';
  size?: 'default' | 'xsmall' | 'small' | 'large' | 'xlarge';
  color?: string;
  tooltip?: string | null;
}

const Avatar: React.FC<AvatarProps> = ({
  classes,
  className: classNameOverriden,
  children: initialChildren,
  variant = 'default',
  size = 'default',
  color,
  tooltip = '',
  onClick,
  ...rest
}) => {
  const children = useMemo(() => initialChildren || <PersonIcon className={classes.personIcon} />, [
    initialChildren,
    classes.personIcon,
  ]);

  const style = useMemo(() => {
    if (!color) {
      return;
    }
    if (variant === 'group') {
      return {
        backgroundColor: color,
        border: '1px solid white',
        boxShadow: `6px 0 0 -1px ${fade(color, 0.8)}, 5px 0 0 1px white, 12px 0 0 -1px ${fade(color, 0.6)}`,
        marginLeft: -4,
      };
    }
    return {
      backgroundColor: color,
    };
  }, [variant, color]);

  const className = useMemo(
    () =>
      classnames(
        {
          [classes.sizeXSmall]: size === 'xsmall',
          [classes.sizeSmall]: size === 'small',
          [classes.sizeLarge]: size === 'large',
          [classes.sizeXLarge]: size === 'xlarge',
          [classes.button]: !!onClick,
        },
        classNameOverriden,
      ),
    [classes, classNameOverriden, size, onClick],
  );

  // TODO skip Tooltip wrapper if not necessary + fix issues with nested styles
  return (
    <Tooltip className={className} title={tooltip} placement="top" hideArrow>
      <MuiAvatar className={className} style={style} onClick={onClick} {...rest}>
        {children}
      </MuiAvatar>
    </Tooltip>
  );
};

export default withStyles(styles)(Avatar);
